import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import Input from "../../componentes/input/input";
import Textarea from "../../componentes/input/textarea";
import Button from "../../componentes/button/button";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import { Loading } from "../../componentes/loading/loading";
import Pagination from "../../componentes/pagination/Pagination";
import ProdutoCatalogoService from "../../services/resource/produtoCatalogoService";
import CentroCustoService from "../../services/resource/CentroCustoService";
import ContaContabilService from "../../services/resource/ContaContabilService";

const service = new ProdutoCatalogoService();
const centroCustoService = new CentroCustoService();
const contaContabilService = new ContaContabilService();

const UNIDADE_MEDIDA_OPTIONS = [
    { value: "UN", label: "Unidade" },
    { value: "KG", label: "Quilograma" },
    { value: "LT", label: "Litro" },
];

const inicial = {
    id: null,
    nome: "",
    unidadeMedida: "",
    categoria: "",
    descricao: "",
    observacoes: "",
    centroCustoCodigo: null,
    contaContabilCodigo: null,
    ativo: true,
};

// Página manual (sem CrudTemplate) — o cadastro de Produto já tem campos demais e um combo de
// Conta Contábil com milhares de linhas (precisa de virtual scroll, que o CrudTemplate/CustomDropdown
// não suportam) para continuar caindo no template genérico de CRUD.
export default function ProdutoCatalogoCrud() {
    const [produtos, setProdutos] = useState([]);
    const [centrosCusto, setCentrosCusto] = useState([]);
    const [contasContabeis, setContasContabeis] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [paginaAtual, setPaginaAtual] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [editando, setEditando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [form, setForm] = useState(inicial);

    const [inativarModalOpen, setInativarModalOpen] = useState(false);
    const [paraInativar, setParaInativar] = useState(null);
    const [inativando, setInativando] = useState(false);

    const [modoEdicao, setModoEdicao] = useState(false);
    const [rascunhos, setRascunhos] = useState({});
    const [confirmandoId, setConfirmandoId] = useState(null);

    useEffect(() => {
        document.title = "MyDash | Gerenciar Produtos";
        carregar();
        centroCustoService.listar().then(res => setCentrosCusto(res.data)).catch(() => setCentrosCusto([]));
        // Só contas de despesa (código começando com "4") — Conta Contábil tem muitos registros,
        // por isso o dropdown abaixo também usa virtualScrollerOptions (virtual scroll).
        contaContabilService.listarPorPrefixo("4").then(res => setContasContabeis(res.data)).catch(() => setContasContabeis([]));
    }, []);

    function carregar() {
        setLoading(true);
        return service.listar()
            .then(res => {
                setProdutos(res.data);
                return res.data;
            })
            .catch(error => {
                console.error("Erro ao carregar produtos", error);
                return [];
            })
            .finally(() => setLoading(false));
    }

    const produtosFiltrados = useMemo(() => {
        const termo = search.trim().toLowerCase();
        if (!termo) return produtos;
        return produtos.filter(p =>
            [p.nome, p.descricao, p.categoria, p.unidadeMedida, p.centroCustoDescricao, p.contaContabilDescricao]
                .some(valor => valor?.toLowerCase().includes(termo))
        );
    }, [produtos, search]);

    function set(campo, valor) {
        setForm(prev => ({ ...prev, [campo]: valor }));
    }

    function abrirModal(produto = null) {
        if (produto) {
            setForm({
                id: produto.id,
                nome: produto.nome || "",
                unidadeMedida: produto.unidadeMedida || "",
                categoria: produto.categoria || "",
                descricao: produto.descricao || "",
                observacoes: produto.observacoes || "",
                centroCustoCodigo: produto.centroCustoCodigo ?? null,
                contaContabilCodigo: produto.contaContabilCodigo ?? null,
                ativo: produto.ativo ?? true,
            });
            setEditando(true);
        } else {
            setForm(inicial);
            setEditando(false);
        }
        setModalOpen(true);
    }

    function fecharModal() {
        if (salvando) return;
        setModalOpen(false);
    }

    function salvar(e) {
        e.preventDefault();
        if (!form.nome.trim()) {
            toast.error("Informe o nome do produto.");
            return;
        }

        setSalvando(true);
        const acao = editando ? service.atualizar(form.id, form) : service.salvar(form);
        acao
            .then(() => {
                toast.success(editando ? "Produto atualizado com sucesso!" : "Produto cadastrado com sucesso!");
                setModalOpen(false);
                carregar();
            })
            .catch(error => console.error("Erro ao salvar produto", error))
            .finally(() => setSalvando(false));
    }

    function confirmarInativacao(produto) {
        setParaInativar(produto);
        setInativarModalOpen(true);
    }

    function inativarConfirmado() {
        setInativando(true);
        service.excluir(paraInativar.id)
            .then(() => {
                toast.success("Produto inativado com sucesso!");
                setInativarModalOpen(false);
                carregar();
            })
            .catch(error => console.error("Erro ao inativar produto", error))
            .finally(() => setInativando(false));
    }

    function montarRascunhos(lista) {
        const mapa = {};
        lista.forEach(p => {
            mapa[p.id] = {
                nome: p.nome || "",
                unidadeMedida: p.unidadeMedida || "",
                categoria: p.categoria || "",
                centroCustoCodigo: p.centroCustoCodigo ?? null,
                contaContabilCodigo: p.contaContabilCodigo ?? null,
                ativo: p.ativo ?? true,
            };
        });
        return mapa;
    }

    // Alterna a tabela inteira entre exibição e edição inline (linha a linha, cada uma confirmada
    // individualmente) — separado do modal de "Adicionar Produto", que continua para criação.
    function alternarModoEdicao() {
        if (modoEdicao) {
            setModoEdicao(false);
            setRascunhos({});
        } else {
            setRascunhos(montarRascunhos(produtos));
            setModoEdicao(true);
        }
    }

    function setRascunhoCampo(id, campo, valor) {
        setRascunhos(prev => ({ ...prev, [id]: { ...prev[id], [campo]: valor } }));
    }

    // Descrição/Observações não têm campo na tabela — preserva os valores atuais do produto ao
    // salvar só os campos editáveis inline.
    function confirmarLinha(produto) {
        const rascunho = rascunhos[produto.id];
        if (!rascunho.nome.trim()) {
            toast.error("Informe o nome do produto.");
            return;
        }

        setConfirmandoId(produto.id);
        service.atualizar(produto.id, {
            id: produto.id,
            ...rascunho,
            descricao: produto.descricao,
            observacoes: produto.observacoes,
        })
            .then(() => {
                toast.success(`"${rascunho.nome}" atualizado!`);
                return carregar();
            })
            .then(lista => setRascunhos(montarRascunhos(lista)))
            .catch(error => console.error("Erro ao atualizar produto", error))
            .finally(() => setConfirmandoId(null));
    }

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            <div className="mx-auto flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <h1 className="font-display text-2xl font-bold text-foreground">Gerenciar Produtos</h1>
                    <div className="flex gap-2">
                        <Button
                            variant={modoEdicao ? "red" : "outline"}
                            label={modoEdicao ? "Sair do Modo de Edição" : "Modo de Edição"}
                            iconLeft={modoEdicao ? "fa fa-times" : "fa fa-pen-to-square"}
                            onClick={alternarModoEdicao}
                        />
                        <Button variant="blue" label="Adicionar Produto" iconLeft="fa fa-plus" onClick={() => abrirModal()} disabled={modoEdicao} />
                    </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="relative w-full md:w-80">
                        <i className="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm" />
                        <input
                            type="search"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Buscar por nome, categoria, descrição..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-input rounded-lg bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                        />
                    </div>
                    <span className="text-sm text-muted-foreground">
                        <strong className="text-foreground font-semibold">{produtosFiltrados.length}</strong> produto(s)
                    </span>
                </div>

                {loading ? (
                    <Loading />
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-border">
                        <table className="min-w-full divide-y divide-border">
                            <thead className="bg-table-header">
                                <tr>
                                    <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Nome</th>
                                    <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Unidade</th>
                                    <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Categoria</th>
                                    <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Centro de Custo</th>
                                    <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Conta Contábil</th>
                                    <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Ativo</th>
                                    <th className="py-3 px-6 text-right text-xs font-medium text-table-header-fg uppercase tracking-wider">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border bg-card">
                                {paginaAtual.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-6 text-center text-sm text-muted-foreground">
                                            Nenhum produto encontrado
                                        </td>
                                    </tr>
                                ) : (
                                    paginaAtual.map(produto => {
                                        const rascunho = rascunhos[produto.id];
                                        const editandoLinha = modoEdicao && !!rascunho;

                                        if (!editandoLinha) {
                                            return (
                                                <tr key={produto.id} className="text-xs transition-colors duration-150 hover:bg-table-row-hover">
                                                    <td className="px-6 py-3 whitespace-nowrap font-medium text-foreground">{produto.nome}</td>
                                                    <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">{produto.unidadeMedida || "—"}</td>
                                                    <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">{produto.categoria || "—"}</td>
                                                    <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">{produto.centroCustoDescricao || "—"}</td>
                                                    <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">{produto.contaContabilDescricao || "—"}</td>
                                                    <td className="px-6 py-3 whitespace-nowrap">
                                                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${produto.ativo ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                                                            {produto.ativo ? "Sim" : "Não"}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-right">
                                                        <div className="flex justify-end gap-2">
                                                            <i onClick={() => abrirModal(produto)} title="Editar" className="fa fa-pencil-square text-primary hover:text-primary/80 p-2 rounded-full hover:bg-primary/10 bg-blue-100 cursor-pointer" />
                                                            {produto.ativo && (
                                                                <i onClick={() => confirmarInativacao(produto)} title="Inativar" className="fa fa-ban text-destructive hover:text-destructive/80 p-2 rounded-full hover:bg-destructive/10 bg-red-50 cursor-pointer" />
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        return (
                                            <tr key={produto.id} className="text-xs bg-primary/5">
                                                <td className="px-3 py-2 min-w-40">
                                                    <input
                                                        type="text"
                                                        value={rascunho.nome}
                                                        onChange={e => setRascunhoCampo(produto.id, "nome", e.target.value)}
                                                        className="w-full h-8 px-2 text-xs border border-input rounded-md bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                                                    />
                                                </td>
                                                <td className="px-3 py-2 min-w-32">
                                                    <CustomDropdown
                                                        value={rascunho.unidadeMedida}
                                                        onChange={e => setRascunhoCampo(produto.id, "unidadeMedida", e.value)}
                                                        options={UNIDADE_MEDIDA_OPTIONS}
                                                        placeholder="Selecione"
                                                        showClear
                                                    />
                                                </td>
                                                <td className="px-3 py-2 min-w-32">
                                                    <input
                                                        type="text"
                                                        value={rascunho.categoria}
                                                        onChange={e => setRascunhoCampo(produto.id, "categoria", e.target.value)}
                                                        className="w-full h-8 px-2 text-xs border border-input rounded-md bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                                                    />
                                                </td>
                                                <td className="px-3 py-2 min-w-44">
                                                    <CustomDropdown
                                                        value={rascunho.centroCustoCodigo}
                                                        onChange={e => setRascunhoCampo(produto.id, "centroCustoCodigo", e.value)}
                                                        options={centrosCusto}
                                                        optionValue="codigo"
                                                        optionLabel="descricao"
                                                        placeholder="Selecione"
                                                        filter
                                                        showClear
                                                    />
                                                </td>
                                                <td className="px-3 py-2 min-w-52">
                                                    <Dropdown
                                                        value={rascunho.contaContabilCodigo}
                                                        onChange={e => setRascunhoCampo(produto.id, "contaContabilCodigo", e.value)}
                                                        options={contasContabeis}
                                                        optionLabel="descricao"
                                                        optionValue="codigo"
                                                        itemTemplate={conta => `${conta.codigo} — ${conta.descricao}`}
                                                        valueTemplate={(conta, opts) => conta ? `${conta.codigo} — ${conta.descricao}` : opts.placeholder}
                                                        filter
                                                        showClear
                                                        virtualScrollerOptions={{ itemSize: 38 }}
                                                        placeholder="Selecione"
                                                        className="w-full text-xs"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <InputSwitch checked={!!rascunho.ativo} onChange={e => setRascunhoCampo(produto.id, "ativo", e.value)} />
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-right">
                                                    <Button
                                                        variant="green"
                                                        label="Confirmar"
                                                        iconLeft="fa fa-check"
                                                        loading={confirmandoId === produto.id}
                                                        onClick={() => confirmarLinha(produto)}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>

                        <div className="px-4 py-3 border-t border-border">
                            <Pagination data={produtosFiltrados} currentData={setPaginaAtual} pageSizeOptions={[20, 50, 100]} />
                        </div>
                    </div>
                )}
            </div>

            <Dialog
                header={editando ? "Editar Produto" : "Cadastrar Produto"}
                visible={modalOpen}
                onHide={fecharModal}
                blockScroll
                className="w-[95%] max-w-4xl"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button variant="outline" label="Cancelar" onClick={fecharModal} disabled={salvando} />
                        <Button
                            type="submit"
                            variant="blue"
                            label={editando ? "Atualizar" : "Salvar"}
                            iconLeft="fa fa-check"
                            loading={salvando}
                            onClick={salvar}
                        />
                    </div>
                }
            >
                <form className="p-4 flex flex-col gap-4" onSubmit={salvar}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input label="Nome" required value={form.nome} onChange={e => set("nome", e.target.value)} />
                        <Input label="Categoria" value={form.categoria} onChange={e => set("categoria", e.target.value)} />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Unidade de Medida</label>
                            <CustomDropdown
                                value={form.unidadeMedida}
                                onChange={e => set("unidadeMedida", e.value)}
                                options={UNIDADE_MEDIDA_OPTIONS}
                                placeholder="Selecione"
                                showClear
                            />
                        </div>

                        {editando && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-foreground/80">Ativo</label>
                                <div className="h-9 flex items-center">
                                    <InputSwitch checked={!!form.ativo} onChange={e => set("ativo", e.value)} />
                                </div>
                            </div>
                        )}
                    </div>

                    <Textarea label="Descrição" rows={2} value={form.descricao} onChange={e => set("descricao", e.target.value)} />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Centro de Custo (opcional)</label>
                            <CustomDropdown
                                value={form.centroCustoCodigo}
                                onChange={e => set("centroCustoCodigo", e.value)}
                                options={centrosCusto}
                                optionValue="codigo"
                                optionLabel="descricao"
                                placeholder="Selecione"
                                filter
                                showClear
                            />
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Conta Contábil (opcional)</label>
                            <Dropdown
                                value={form.contaContabilCodigo}
                                onChange={e => set("contaContabilCodigo", e.value)}
                                options={contasContabeis}
                                optionLabel="descricao"
                                optionValue="codigo"
                                itemTemplate={conta => `${conta.codigo} — ${conta.descricao}`}
                                valueTemplate={(conta, opts) => conta ? `${conta.codigo} — ${conta.descricao}` : opts.placeholder}
                                filter
                                showClear
                                virtualScrollerOptions={{ itemSize: 38 }}
                                placeholder="Selecione"
                                className="w-full text-sm"
                            />
                        </div>
                    </div>
                </form>
            </Dialog>

            <Dialog
                header="Inativar Produto"
                visible={inativarModalOpen}
                onHide={() => !inativando && setInativarModalOpen(false)}
                blockScroll
                className="w-[95%] max-w-md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button variant="outline" label="Cancelar" onClick={() => setInativarModalOpen(false)} disabled={inativando} />
                        <Button variant="red" label="Inativar" iconLeft="fa fa-ban" loading={inativando} onClick={inativarConfirmado} />
                    </div>
                }
            >
                <div className="p-4">
                    <p className="text-sm text-foreground">
                        Deseja realmente inativar o produto <strong>{paraInativar?.nome}</strong>?
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">
                        Ele deixa de aparecer no autocomplete de novas solicitações, mas continua visível nas
                        solicitações que já o referenciam.
                    </p>
                </div>
            </Dialog>
        </div>
    );
}
